import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { Camera, UserPlus, Trash2, Upload, Check } from 'lucide-react';
import { getFullFileUrl } from '../../services/profileService';
import './AvatarUpload.css';

const AvatarUpload = ({ currentImage, onUpload, loading, userId }) => {
    const [preview, setPreview] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Canvas cropping state variables
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageObj, setImageObj] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [rawSelectedFile, setRawSelectedFile] = useState(null);
    const canvasRef = useRef(null);

    // Sync preview with currentImage prop
    useEffect(() => {
        if (currentImage) {
            setPreview(getFullFileUrl(currentImage, 'profilePhoto', userId));
        } else {
            setPreview(null);
        }
    }, [currentImage, userId]);

    // Canvas drawing effect for crop preview
    useEffect(() => {
        if (!imageObj || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const containerSize = 300;

        ctx.clearRect(0, 0, containerSize, containerSize);
        ctx.save();

        const ratio = Math.max(containerSize / imageObj.width, containerSize / imageObj.height);
        const drawWidth = imageObj.width * ratio * zoom;
        const drawHeight = imageObj.height * ratio * zoom;

        const x = (containerSize - drawWidth) / 2 + offset.x;
        const y = (containerSize - drawHeight) / 2 + offset.y;

        ctx.drawImage(imageObj, x, y, drawWidth, drawHeight);
        ctx.restore();

        // Draw overlay mask with circular cut-out
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.beginPath();
        ctx.rect(0, 0, containerSize, containerSize);
        ctx.arc(150, 150, 100, 0, Math.PI * 2, true);
        ctx.fill();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(150, 150, 100, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
    }, [imageObj, zoom, offset]);

    const handleCanvasMouseDown = (e) => {
        if (!imageObj) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - offset.x,
            y: e.clientY - offset.y
        });
    };

    const handleCanvasMouseMove = (e) => {
        if (!isDragging || !imageObj) return;
        setOffset({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleCanvasMouseUp = () => {
        setIsDragging(false);
    };

    const handleCanvasTouchStart = (e) => {
        if (!imageObj || e.touches.length === 0) return;
        setIsDragging(true);
        setDragStart({
            x: e.touches[0].clientX - offset.x,
            y: e.touches[0].clientY - offset.y
        });
    };

    const handleCanvasTouchMove = (e) => {
        if (!isDragging || !imageObj || e.touches.length === 0) return;
        setOffset({
            x: e.touches[0].clientX - dragStart.x,
            y: e.touches[0].clientY - dragStart.y
        });
    };

    const handleCloseCropModal = () => {
        setShowCropModal(false);
        setCropImageSrc(null);
        setImageObj(null);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setRawSelectedFile(null);
    };

    const handleSaveCroppedPhoto = async () => {
        if (!imageObj || !rawSelectedFile) return;

        try {
            const containerSize = 300;
            const ratio = Math.max(containerSize / imageObj.width, containerSize / imageObj.height);
            const drawWidth = imageObj.width * ratio * zoom;
            const drawHeight = imageObj.height * ratio * zoom;
            const x = (containerSize - drawWidth) / 2 + offset.x;
            const y = (containerSize - drawHeight) / 2 + offset.y;

            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = 400;
            cropCanvas.height = 400;
            const cropCtx = cropCanvas.getContext('2d');

            const targetWidth = drawWidth * 2;
            const targetHeight = drawHeight * 2;
            const targetX = (x - 50) * 2;
            const targetY = (y - 50) * 2;

            cropCtx.drawImage(imageObj, targetX, targetY, targetWidth, targetHeight);

            const mimeType = rawSelectedFile.type || 'image/jpeg';
            const croppedFile = await new Promise((resolve) => {
                cropCanvas.toBlob((blob) => {
                    resolve(new File([blob], rawSelectedFile.name || 'cropped_avatar.jpg', {
                        type: mimeType,
                        lastModified: Date.now()
                    }));
                }, mimeType, 0.9);
            });

            // Set local preview to data URL so they see the result instantly
            setPreview(cropCanvas.toDataURL(mimeType));

            // Upload the cropped file
            onUpload(croppedFile);
            handleCloseCropModal();
        } catch (error) {
            console.error('Error cropping avatar:', error);
            alert('Failed to crop and adjust the image. Please try again.');
        }
    };

    const handleFileSelect = (file) => {
        if (!file) return;

        // Validate file type (added webp support!)
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be less than 5MB');
            return;
        }

        setRawSelectedFile(file);

        // Open cropping UI
        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImageSrc(e.target.result);
            setZoom(1);
            setOffset({ x: 0, y: 0 });
            setShowCropModal(true);

            const img = new Image();
            img.onload = () => {
                setImageObj(img);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemove = () => {
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onUpload(null); // Pass null to trigger profilePhoto delete
    };

    return (
        <div className="avatar-upload">
            <div 
                className={`avatar-upload-area ${dragOver ? 'drag-over' : ''} ${loading ? 'loading' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                {preview ? (
                    <div className="avatar-preview">
                        <img src={preview} alt="Profile preview" className="avatar-image" />
                        <div className="avatar-overlay">
                            <Camera size={24} className="mb-1" />
                            <span>Change Photo</span>
                        </div>
                        {loading && (
                            <div className="upload-loading">
                                <div className="spinner-border spinner-border-sm"></div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="avatar-placeholder">
                        <UserPlus size={40} className="mb-2 text-muted" />
                        <h6>Upload Photo</h6>
                        <p>Drag & drop or click to select</p>
                        <small>JPEG, PNG, WEBP, GIF up to 5MB</small>
                        {loading && (
                            <div className="upload-loading">
                                <div className="spinner-border spinner-border-sm"></div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
            />

            {preview && (
                <div className="avatar-actions mt-3 d-flex justify-content-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRemove}
                        disabled={loading}
                        className="me-2 d-flex align-items-center"
                    >
                        <Trash2 size={14} className="me-2" />
                        Remove
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleClick}
                        disabled={loading}
                        className="d-flex align-items-center"
                    >
                        <Camera size={14} className="me-2" />
                        Change
                    </Button>
                </div>
            )}

            {/* Premium Photo Adjustment Modal */}
            <Modal show={showCropModal} onHide={handleCloseCropModal} centered className="profile-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold d-flex align-items-center">
                        <Camera size={20} className="me-2 text-primary" />
                        Adjust Profile Photo
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-4">
                    <div className="crop-container d-flex flex-column align-items-center mb-4">
                        <div 
                            className="position-relative overflow-hidden border border-2 border-light shadow-sm"
                            style={{ width: '300px', height: '300px', borderRadius: '12px' }}
                        >
                            <canvas
                                ref={canvasRef}
                                width={300}
                                height={300}
                                onMouseDown={handleCanvasMouseDown}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={handleCanvasMouseUp}
                                onTouchStart={handleCanvasTouchStart}
                                onTouchMove={handleCanvasTouchMove}
                                onTouchEnd={handleCanvasMouseUp}
                                style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'block' }}
                            />
                        </div>
                        <div className="w-100 mt-3 px-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted small fw-bold text-uppercase">Zoom & Position</span>
                                <span className="badge bg-light text-primary">{Math.round(zoom * 100)}%</span>
                            </div>
                            <Form.Range
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                className="custom-range"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={handleCloseCropModal} className="px-4">
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveCroppedPhoto}
                        className="px-4 d-flex align-items-center"
                    >
                        <Check size={18} className="me-2" />
                        Apply Crop
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AvatarUpload;